import { inject, Injectable } from '@angular/core';
import { NavigationEnd, NavigationExtras, Router } from '@angular/router';
import { NavController } from '@ionic/angular';
import {
  filter,
  map,
  Observable,
  of,
  pairwise,
  ReplaySubject,
  share,
  startWith,
  Subject,
  switchMap,
} from 'rxjs';
import { PagesConfig } from '../settings/app-init-settings.interfaces';
import { AppInitSettingsService } from '../settings/app-init-settings.service';

type RouteParam = Record<string, number | string>;

type NavigationServiceOptions = {
  /**
   * Optional route params.
   */
  params?: (string | number)[] | RouteParam;
  /**
   * Whether to show a back animation.
   */
  isNavigateBack?: boolean;
  /**
   * Navigation options, see NavController documentation ('@ionic/angular').
   */
  navigationOptions?: NavigationExtras;
  /**
   * Optional query params.
   */
  queryParams?: Record<string, string>;
  shouldPreventReload?: boolean;
};

export type NavigateByGroupAndTypeArgs = {
  group: string;
  type: string;
} & NavigationServiceOptions;

export interface NavigationParams {
  path: string;
  params: (string | number)[] | RouteParam;
  isNavigateBack: boolean;
  options?: NavigationExtras;
  queryParams?: Record<string, string>;
  shouldPreventReload: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private readonly appInitService = inject(AppInitSettingsService);
  private readonly navCtrl = inject(NavController);
  private readonly router = inject(Router);

  private readonly navigationCalledSubject$ =
    new ReplaySubject<NavigationParams>();
  readonly navigationCalled$ = this.navigationCalledSubject$.asObservable();
  private readonly routerNavigationEndSubject$ = new ReplaySubject<string>(1);
  readonly routerNavigationEnd$ = this.createRouterNavigationEnd$(
    this.routerNavigationEndSubject$,
  );

  private readonly reloadPage = new Subject<boolean>();

  getReloadPage$(): Observable<boolean> {
    return this.reloadPage.asObservable();
  }

  setReloadPage(isReload: boolean): void {
    this.reloadPage.next(isReload);
  }

  /**
   * Navigate to the route with the provided name.
   * @param name name of the route
   * @param NavigationServiceOptions arguments
   * @returns true if success
   */
  async navigateByName(
    name: string,
    {
      params = [],
      isNavigateBack = false,
      navigationOptions,
      queryParams,
      shouldPreventReload = true,
    }: NavigationServiceOptions = {},
  ): Promise<boolean> {
    const route = this.getPagesConfiguration()
      ?.flatMap((group) => group.pages)
      .find((page) => page.name === name);

    if (!route) {
      console.error(`config for route with name ${name} not found`);
      return false;
    }

    await this.navigate(
      route?.path,
      params,
      isNavigateBack,
      navigationOptions,
      queryParams,
      shouldPreventReload,
    );
    return true;
  }

  /**
   * Navigate to the route with the provided group and type.
   * @param NavigationServiceOptions arguments
   * @returns true if success
   */
  async navigateByGroupAndType(
    group: string,
    type: string,
    {
      params = [],
      isNavigateBack = false,
      navigationOptions,
      queryParams,
      shouldPreventReload = true,
    }: NavigationServiceOptions = {},
  ): Promise<boolean> {
    const route = this.getPagesConfiguration()
      ?.find((x) => x.group === group)
      ?.pages?.find((x) => x.type === type);

    if (!route) {
      console.error(
        `config for route with group ${group} and type ${type} not found`,
      );
    }

    await this.navigate(
      route?.path || '',
      params,
      isNavigateBack,
      navigationOptions,
      queryParams,
      shouldPreventReload,
    );
    return true;
  }

  onPageEntered$(pagePath: string): Observable<void> {
    return this.router.events.pipe(
      filter((event) => {
        return (
          event instanceof NavigationEnd &&
          (event as NavigationEnd).url === pagePath
        );
      }),
      map(() => undefined),
    );
  }

  triggerRouterNavigationEnd(urlAfterRedirects: string): void {
    this.routerNavigationEndSubject$.next(urlAfterRedirects);
  }

  private createRouterNavigationEnd$(
    routerNavigationEndSubject$: ReplaySubject<string>,
  ): Observable<{
    previousUrl: string | null;
    currentUrl: string;
  }> {
    return routerNavigationEndSubject$.asObservable().pipe(
      startWith(null),
      pairwise(),
      switchMap(([previousUrl, currentUrl]) => {
        return of({
          previousUrl,
          currentUrl: currentUrl || '',
        });
      }),
      share(),
    );
  }

  /**
   * Navigate to the provided path
   * @param path path to navigate to
   * @param params optional route params
   * @param isNavigateBack whether to show back animation
   * @param options navigation options, see NavController documentation ('@ionic/angular')
   */
  private async navigate(
    path: string,
    params: (string | number)[] | RouteParam = [],
    isNavigateBack = false,
    options?: NavigationExtras,
    queryParams?: Record<string, string>,
    shouldPreventReload = true,
  ): Promise<void> {
    this.navigationCalledSubject$.next({
      path,
      params,
      isNavigateBack,
      options,
      queryParams,
      shouldPreventReload,
    });

    let url: string;

    if (params.length === 0) {
      url = path;
    } else if (Array.isArray(params)) {
      url = [path, ...params].join('/');
    } else {
      url = this.replaceParamPlaceholders(path, params as RouteParam);
    }

    if (queryParams) {
      const queryParamsString = Object.entries(queryParams)
        .map(([key, value]) => `${key}=${value}`)
        .join('&');
      url = `${url}?${queryParamsString}`;
    }

    if (shouldPreventReload && this.router.url === url) {
      return;
    }

    if (isNavigateBack) {
      await this.navCtrl.navigateBack(url, options);
    } else {
      await this.navCtrl.navigateForward(url, options);
    }
  }

  private replaceParamPlaceholders(route: string, params: RouteParam): string {
    const notFound: string[] = [];

    Object.entries(params).forEach(([name, value]) => {
      const stringValue = value.toString();
      const result = route.replace(`:${name}`, stringValue);
      if (result === route) {
        notFound.push(stringValue);
      } else {
        route = result;
      }
    });

    const parts = [route, ...notFound];
    return parts.join('/');
  }

  private getPagesConfiguration(): PagesConfig[] | undefined {
    return (
      this.appInitService.configuration as { pagesConfig?: PagesConfig[] }
    ).pagesConfig;
  }
}
